//home-page.tsx

import React, { useState, Fragment, useContext } from "react";
import { useRef, useEffect } from "react";
import {
  CssBaseline,
  Drawer,
  AppBar,
  Chip,
  Tooltip,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Box,
} from "@mui/material";
import CropLandscapeIcon from "@mui/icons-material/CropLandscape";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import TimelineIcon from "@mui/icons-material/Timeline";
import AccountCircle from "@mui/icons-material/AccountCircle";
import HelpIcon from "@mui/icons-material/Help";
import SettingsIcon from "@mui/icons-material/Settings";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import MenuIcon from "@mui/icons-material/Menu";
import ListIcon from "@mui/icons-material/List";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import GrainIcon from "@mui/icons-material/Grain";
import InventoryIcon from "@mui/icons-material/Inventory";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import ForumIcon from "@mui/icons-material/Forum";
import { Route, Switch, Redirect, Link as RouterLink } from "react-router-dom";
import { ListItemLink, sidebarListItemSx } from "../components/list-items";
import { ErrorContext, UserContext } from "../components/app-routes";
import LoadingBackdrop from "../components/loading-backdrop";
import SetTimeZone from "../components/set-time-zone";
import logo from "../assets/logo.png";
import mixpanel from "mixpanel-browser";
import YouTube, { YouTubeProps } from "react-youtube";
import { onboardingsApi as onboardingApi } from "../services/api/onboardings";
import { growersApi } from "../services/api/growers";
import { Grower } from "../models/grower";

import CropsPage from "./crops-page";
import AddCrop from "../components/add-crop";
import CropPage from "./crop-page";
import CustomersPage from "./customers-page";
import AddCustomerPage from "./add-customer-page";
import CustomerPage from "./customer-page";
import Products from "./products-page";
import AddProductPage from "./add-product-page";
import ProductPage from "./product-page";
import OrdersPage from "./orders-page";
import AddOrderPage from "./add-order-page";
import OrderPage from "./order-page";
import TasksPage from "./tasks-page";
import ReportsPage from "./reports-page";
import SeedsPage from "./seeds-page";
import SeedPage from "./seed-page";
import AddSeedPage from "./add-seed-page";
import DeliveriesPage from "./deliveries-page";
import SettingsPage from "./settings-page";
import DiaryPage from "./diary-page";
import AddDiaryPage from "./add-and-edit-diary-page";
import SupportPage from "./support-page";
import DynamicSupportCategoryPage from "./dynamic-support-category-page";
import DynamicSupportArticlePage from "./dynamic-support-article-page";
import CropTrackerPage from "./crop-tracker-page";
import StockAndAuditPage from "./stock-and-audit-page";
import CategoryIcon from "@mui/icons-material/Category";
import { ListItemButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShareIcon from "@mui/icons-material/Share";
import ReferralsPage from "./referrals-page";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AdminReferralsPage from "./admin-referrals-page";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SupportAdminPage from "./support-admin-page";
import TeamPage from "./team-page";
import ProPlaceholderPage from "./pro-placeholder-page";
import {
  LowStockProvider,
  LowStockSummary,
} from "../components/low-stock-alerts";
import PkgPage from "./pkg-page";
import PkgsPage from "./pkgs-page";
import TrayPage from "./tray-page";
import TraysPage from "../pages/trays-page";
import AddTrayPage from "./add-tray-page";
import { getCustomLabel } from "../utils/custom-labels";

const drawerWidth = 240;

export default function HomePage(props: {
  onLogOut: () => void;
  onGrowerUpdated: () => void;
}) {
  const context = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] =
    useState<Element | null>(null);

  const submenuRef = useRef<HTMLDivElement | null>(null);

  const [elementsOpen, setElementsOpen] = useState(false);

  const [inputsAnchorEl, setInputsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const handleToggleInputs = (e: React.MouseEvent<HTMLDivElement>) => {
    setElementsAnchorEl(null);

    if (inputsAnchorEl) {
      setInputsAnchorEl(null);
    } else {
      setInputsAnchorEl(e.currentTarget);
    }
  };

  const [elementsAnchorEl, setElementsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const handleToggleElements = (e: React.MouseEvent<HTMLDivElement>) => {
    setInputsAnchorEl(null);

    if (elementsAnchorEl) {
      setElementsAnchorEl(null);
    } else {
      setElementsAnchorEl(e.currentTarget);
    }
  };

  const [communityAnchorEl, setCommunityAnchorEl] =
    useState<null | HTMLElement>(null);

  const handleToggleCommunity = (e: React.MouseEvent<HTMLDivElement>) => {
    setElementsAnchorEl(null);
    setInputsAnchorEl(null);

    if (communityAnchorEl) {
      setCommunityAnchorEl(null);
    } else {
      setCommunityAnchorEl(e.currentTarget);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        elementsAnchorEl &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node) &&
        !elementsAnchorEl.contains(event.target as Node)
      ) {
        setElementsAnchorEl(null);
      }

      if (
        inputsAnchorEl &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node) &&
        !inputsAnchorEl.contains(event.target as Node)
      ) {
        setInputsAnchorEl(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [elementsAnchorEl, inputsAnchorEl, communityAnchorEl]);

  const grower = context.grower;
  if (!grower) throw new Error("Invalid grower");

  console.log("home grower plan", grower.Plan);

  const [openOnboardingDialog, setOpenOnboardingDialog] = useState(
    grower?.Onboarding.NewUserVideo !== true,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const handleSetTimeZoneOnClickOk = async (timeZone: string) => {
    setBusy(true);
    try {
      await growersApi.patch({ timeZone });
      props.onGrowerUpdated();
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleClickLink = () => setMobileOpen(false);

  const handleClickProfileMenu: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => setProfileMenuAnchorEl(e.currentTarget);
  const handleCloseProfileMenu = () => setProfileMenuAnchorEl(null);

  const handleClickLogOut = () => {
    setProfileMenuAnchorEl(null);
    props.onLogOut();
  };

  const handleCloseOnboardingDialog = () => {
    setOpenOnboardingDialog(false);
    mixpanel.track("Snooze Onboarding Dialog");
  };

  const handleCompleteOnboardingDialog = async () => {
    setBusy(true);
    try {
      await onboardingApi.patch({ NewUserVideo: true });
      mixpanel.track("Complete Onboarding Dialog");
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
      setOpenOnboardingDialog(false);
    }
  };

  const openProfileMenu = Boolean(profileMenuAnchorEl);

  const handleOnPlayOnboardingVideo = () =>
    mixpanel.track("Play New User Video");

  const DrawerListItemLinks = ({ grower }: { grower: Grower }) => (
    <Fragment>
      <Box sx={{ p: 1 }}>
        <img alt="logo" src={logo} width="215" />
      </Box>
      <List>
        <ListItemLink
          to="/home/tasks"
          primary={getCustomLabel(grower, "tasks")}
          icon={<ScheduleIcon />}
          onClick={handleClickLink}
        />
        <ListItemLink
          to="/home/orders"
          primary={getCustomLabel(grower, "orders")}
          icon={<ListIcon />}
          onClick={handleClickLink}
        />
        <ListItemLink
          to="/home/crop-tracker"
          primary={getCustomLabel(grower, "cropTracker")}
          icon={<TimelineIcon />}
          onClick={() => {
            mixpanel.track("Nav Click - Crop Tracker");
            handleClickLink();
          }}
        />

        {/* ELEMENTS SECTION */}
        <ListItemButton onClick={handleToggleElements} sx={sidebarListItemSx}>
          <ListItemIcon>
            <CategoryIcon />
          </ListItemIcon>
          <ListItemText primary={getCustomLabel(grower, "elements")} />{" "}
          <ListItemIcon sx={{ minWidth: 0, ml: "auto" }}>
            <ChevronRightIcon />
          </ListItemIcon>
        </ListItemButton>

        {isMobile
          ? // Mobile version — expand inline
            elementsAnchorEl && (
              <List component="div" disablePadding>
                <ListItemLink
                  to="/home/crops"
                  primary={getCustomLabel(grower, "crops")}
                  icon={<LocalFloristIcon />}
                  onClick={() => {
                    handleClickLink();
                    setElementsAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to="/home/products"
                  primary={getCustomLabel(grower, "products")}
                  icon={<InventoryIcon />}
                  onClick={() => {
                    handleClickLink();
                    setElementsAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to="/home/customers"
                  primary={getCustomLabel(grower, "customers")}
                  icon={<PeopleIcon />}
                  onClick={() => {
                    handleClickLink();
                    setElementsAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to={grower.Plan === "pro" ? "/home/trays" : "#"}
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Trays
                      {grower.Plan !== "pro" && (
                        <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                      )}
                    </Box>
                  }
                  icon={<CropLandscapeIcon />}
                  onClick={() => {
                    handleClickLink();
                    setElementsAnchorEl(null);
                  }}
                  nested
                />
              </List>
            )
          : // Desktop version — flyout menu to the right
            elementsAnchorEl && (
              <Box
                ref={submenuRef}
                sx={{
                  position: "fixed",
                  top: elementsAnchorEl.getBoundingClientRect().top,
                  left: elementsAnchorEl.getBoundingClientRect().right + 8,
                  backgroundColor: "#fcfcfce0",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  boxShadow: "0px 2px 6px rgba(0,0,0,.75)",
                  zIndex: 2000,
                  width: 250,
                  p: 0.5,
                }}
              >
                <List>
                  <ListItemLink
                    to="/home/crops"
                    primary={getCustomLabel(grower, "crops")}
                    icon={<LocalFloristIcon />}
                    onClick={() => {
                      handleClickLink();
                      setElementsAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to="/home/products"
                    primary={getCustomLabel(grower, "products")}
                    icon={<InventoryIcon />}
                    onClick={() => {
                      handleClickLink();
                      setElementsAnchorEl(null);
                    }}
                  />

                  <ListItemLink
                    to="/home/customers"
                    primary={getCustomLabel(grower, "customers")}
                    icon={<PeopleIcon />}
                    onClick={() => {
                      handleClickLink();
                      setElementsAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to={grower.Plan === "pro" ? "/home/trays" : "#"}
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Trays
                        {grower.Plan !== "pro" && (
                          <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                        )}
                      </Box>
                    }
                    icon={<CropLandscapeIcon />}
                    onClick={() => {
                      handleClickLink();
                      setElementsAnchorEl(null);
                    }}
                  />
                </List>
              </Box>
            )}

        {/* INPUTS SECTION */}
        <ListItemButton onClick={handleToggleInputs} sx={sidebarListItemSx}>
          <ListItemIcon>
            <InventoryIcon />
          </ListItemIcon>
          <ListItemText primary="Inputs" />
          <ListItemIcon sx={{ minWidth: 0, ml: "auto" }}>
            <ChevronRightIcon />
          </ListItemIcon>
        </ListItemButton>
        {isMobile
          ? inputsAnchorEl && (
              <List component="div" disablePadding>
                <ListItemLink
                  to="/home/seeds"
                  primary="Seeds"
                  icon={<GrainIcon />}
                  onClick={() => {
                    handleClickLink();
                    setInputsAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to={grower.Plan === "pro" ? "/home/pkgs" : "#"}
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Packaging
                      {grower.Plan !== "pro" && (
                        <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                      )}
                    </Box>
                  }
                  icon={<InventoryIcon />}
                  onClick={() => {
                    handleClickLink();
                    setInputsAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to={grower.Plan === "pro" ? "/home/soil" : "#"}
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Soil
                      {grower.Plan !== "pro" && (
                        <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                      )}
                    </Box>
                  }
                  icon={<LocalFloristIcon />}
                  onClick={() => {
                    handleClickLink();
                    setInputsAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to={grower.Plan === "pro" ? "/home/stock-and-audit" : "#"}
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Stock and Audit
                      {grower.Plan !== "pro" && (
                        <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                      )}
                    </Box>
                  }
                  icon={<QueryStatsIcon />}
                  onClick={() => {
                    handleClickLink();
                    setInputsAnchorEl(null);
                  }}
                  nested
                />
              </List>
            )
          : inputsAnchorEl && (
              <Box
                ref={submenuRef}
                sx={{
                  position: "fixed",
                  top: inputsAnchorEl.getBoundingClientRect().top,
                  left: inputsAnchorEl.getBoundingClientRect().right + 8,
                  backgroundColor: "#fcfcfce0",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  boxShadow: "0px 2px 6px rgba(0,0,0,.75)",
                  zIndex: 2000,
                  width: 250,
                  p: 0.5,
                }}
              >
                <List>
                  <ListItemLink
                    to="/home/seeds"
                    primary="Seeds"
                    icon={<GrainIcon />}
                    onClick={() => {
                      handleClickLink();
                      setInputsAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to={grower.Plan === "pro" ? "/home/pkgs" : "#"}
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Packaging
                        {grower.Plan !== "pro" && (
                          <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                        )}
                      </Box>
                    }
                    icon={<InventoryIcon />}
                    onClick={() => {
                      handleClickLink();
                      setInputsAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to={grower.Plan === "pro" ? "/home/soil" : "#"}
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Soil
                        {grower.Plan !== "pro" && (
                          <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                        )}
                      </Box>
                    }
                    icon={<LocalFloristIcon />}
                    onClick={() => {
                      handleClickLink();
                      setInputsAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to={grower.Plan === "pro" ? "/home/stock-and-audit" : "#"}
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Stock and Audit
                        {grower.Plan !== "pro" && (
                          <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
                        )}
                      </Box>
                    }
                    icon={<QueryStatsIcon />}
                    onClick={() => {
                      handleClickLink();
                      setInputsAnchorEl(null);
                    }}
                  />
                </List>
              </Box>
            )}
        <ListItemLink
          to="/home/deliveries"
          icon={<DirectionsBikeIcon />}
          onClick={handleClickLink}
          primary={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getCustomLabel(grower, "deliveries")}{" "}
              {grower.Plan === "hobby" && (
                <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
              )}
            </Box>
          }
        />
        <ListItemLink
          to="/home/diary"
          primary={getCustomLabel(grower, "diary")}
          icon={<ImportContactsIcon />}
          onClick={() => {
            mixpanel.track("Nav Click - Diary");
            handleClickLink();
          }}
        />

        <ListItemLink
          to="/home/reports"
          icon={<InsertChartIcon />}
          onClick={handleClickLink}
          primary={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getCustomLabel(grower, "reports")}{" "}
              {grower.Plan === "hobby" && (
                <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
              )}
            </Box>
          }
        />
        <Divider />
        <ListItemLink
          to="/home/referrals"
          primary={getCustomLabel(grower, "referrals")}
          icon={<ShareIcon />}
          onClick={handleClickLink}
        />

        <ListItemButton onClick={handleToggleCommunity} sx={sidebarListItemSx}>
          <ListItemIcon>
            <ForumIcon />
          </ListItemIcon>
          <ListItemText primary="Community" />
          <ListItemIcon sx={{ minWidth: 0, ml: "auto" }}>
            <ChevronRightIcon />
          </ListItemIcon>
        </ListItemButton>
        {isMobile
          ? communityAnchorEl && (
              <List component="div" disablePadding>
                <ListItemLink
                  to="/home/support"
                  primary="Support"
                  icon={<HelpIcon />}
                  onClick={() => {
                    handleClickLink();
                    setCommunityAnchorEl(null);
                  }}
                  nested
                />
                {/*
                <ListItemLink
                  to="#"
                  primary="Forum"
                  icon={<ForumIcon />}
                  onClick={() => {
                    window.open("https://community.seedleaf.co", "_blank");
                    setCommunityAnchorEl(null);
                    handleClickLink();
                  }}
                  nested
                />
                */}
              </List>
            )
          : communityAnchorEl && (
              <Box
                ref={submenuRef}
                sx={{
                  position: "fixed",
                  top: communityAnchorEl.getBoundingClientRect().top,
                  left: communityAnchorEl.getBoundingClientRect().right + 8,
                  backgroundColor: "#fcfcfce0",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  boxShadow: "0px 2px 6px rgba(0,0,0,.75)",
                  zIndex: 2000,
                  width: 250,
                  p: 0.5,
                }}
              >
                <List>
                  <ListItemLink
                    to="/home/support"
                    primary="Support"
                    icon={<HelpIcon />}
                    onClick={() => {
                      handleClickLink();
                      setCommunityAnchorEl(null);
                    }}
                  />
                  {/* 
                  <ListItemLink
                    to="#"
                    primary="Forum (External)"
                    icon={<ForumIcon />}
                    onClick={() => {
                      window.open("https://community.seedleaf.co", "_blank");
                      setCommunityAnchorEl(null);
                      handleClickLink();
                    }}
                  />
                  */}
                </List>
              </Box>
            )}
        <ListItemLink
          to="/home/team"
          icon={<PeopleIcon />}
          onClick={() => {
            mixpanel.track("Nav Click - Team", {
              growerId: grower.id,
              plan: grower.Plan,
            });
            handleClickLink();
          }}
          primary={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getCustomLabel(grower, "team")} (Beta){" "}
              {grower.Plan === "hobby" && (
                <LockOutlinedIcon sx={{ ml: 1 }} fontSize="small" />
              )}
            </Box>
          }
        />

        <ListItemLink
          to="/home/settings"
          primary={getCustomLabel(grower, "settings")}
          icon={<SettingsIcon />}
          onClick={handleClickLink}
        />
      </List>
    </Fragment>
  );

  const youTubeOpts: YouTubeProps["opts"] = {
    playerVars: { rel: 0, cc_load_policy: 1, cc_lang_pref: "en" },
  };

  const teamChipBg = "#eeeeee";

  return (
    <LowStockProvider grower={grower}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          {context.user.EmailVerified === false && (
            <Box
              sx={{
                display: "block",
                backgroundColor: theme.palette.primary.light,
                textAlign: "center",
                color: theme.palette.primary.contrastText,
                p: 1,
              }}
            >
              <Button
                component={RouterLink}
                to="/verify-email"
                size="small"
                variant="contained"
                color="primary"
                sx={{ color: "white" }}
              >
                Please verify your email
              </Button>
            </Box>
          )}
          <Toolbar sx={{ justifyContent: "end" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
              size="large"
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Current team">
              <Chip
                size="medium"
                variant="filled"
                label={`Farm: ${grower.Name}`}
                sx={{
                  mr: 1,
                  fontWeight: 400,
                  borderRadius: 5,
                  backgroundColor: teamChipBg,
                  height: 34,
                  "& .MuiChip-label": {
                    px: 1.5,
                    fontSize: 16,
                  },
                }}
              />
            </Tooltip>

            <LowStockSummary />
            <IconButton
              size="medium"
              onClick={handleClickProfileMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={profileMenuAnchorEl}
              open={openProfileMenu}
              onClose={handleCloseProfileMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <MenuItem onClick={handleClickLogOut}>
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText primary="Log out" />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Drawer Navigation */}
        <Box
          component="nav"
          sx={{
            width: { sm: drawerWidth },
            flexShrink: { sm: 0 },
          }}
        >
          {isMobile && (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: "block", sm: "none" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                },
              }}
            >
              <DrawerListItemLinks grower={grower} />
            </Drawer>
          )}
          {isDesktop && (
            <Drawer
              variant="permanent"
              open
              sx={{
                display: { xs: "none", sm: "block" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                },
              }}
            >
              <DrawerListItemLinks grower={grower} />
            </Drawer>
          )}
        </Box>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          <Switch>
            <Route exact path="/home/seeds" component={SeedsPage} />
            <Route exact path="/home/seeds/add" component={AddSeedPage} />
            <Route exact path="/home/seeds/:id" component={SeedPage} />
            <Route path="/home/pkgs" exact>
              {grower.Plan === "pro" ? (
                <PkgsPage />
              ) : (
                <ProPlaceholderPage title="Packaging" />
              )}
            </Route>
            <Route path="/home/pkgs/add" exact>
              {grower.Plan === "pro" ? (
                <PkgPage />
              ) : (
                <ProPlaceholderPage title="Packaging" />
              )}
            </Route>
            <Route path="/home/pkgs/:id" exact>
              {grower.Plan === "pro" ? (
                <PkgPage />
              ) : (
                <ProPlaceholderPage title="Packaging" />
              )}
            </Route>
            <Route exact path="/home/crops" component={CropsPage} />
            <Route exact path="/home/crops/add" component={AddCrop} />
            <Route path="/home/crops/:id" component={CropPage} />
            <Route
              exact
              path="/home/crop-tracker"
              component={CropTrackerPage}
            />
            <Route exact path="/home/customers" component={CustomersPage} />
            <Route
              exact
              path="/home/customers/add"
              component={AddCustomerPage}
            />
            <Route path="/home/customers/:id" component={CustomerPage} />
            <Route exact path="/home/products" component={Products} />
            <Route exact path="/home/products/add" component={AddProductPage} />
            <Route exact path="/home/products/:id" component={ProductPage} />
            <Route exact path="/home/orders" component={OrdersPage} />
            <Route exact path="/home/orders/add" component={AddOrderPage} />
            <Route exact path="/home/orders/:id" component={OrderPage} />
            <Route exact path="/home/tasks" component={TasksPage} />
            <Route exact path="/home/reports" component={ReportsPage} />
            <Route path="/home/deliveries" component={DeliveriesPage} />
            <Route exact path="/home/trays/add">
              <AddTrayPage />
            </Route>
            <Route exact path="/home/trays/:id">
              <TrayPage />
            </Route>
            <Route path="/home/soil">
              <ProPlaceholderPage title="Soil" />
            </Route>
            <Route exact path="/home/trays">
              {grower.Plan === "pro" ? (
                <TraysPage />
              ) : (
                <ProPlaceholderPage title="Trays" />
              )}
            </Route>
            <Route path="/home/stock-and-audit">
              {grower.Plan === "pro" ? (
                <StockAndAuditPage />
              ) : (
                <ProPlaceholderPage title="Stock and Audit" />
              )}
            </Route>
            <Route exact path="/home/team">
              {grower.Plan === "hobby" ? (
                <Redirect to="/home/tasks" />
              ) : (
                <TeamPage />
              )}
            </Route>
            <Route path="/home/settings">
              <SettingsPage onGrowerUpdated={props.onGrowerUpdated} />
            </Route>
            <Route exact path="/home/diary" component={DiaryPage} />
            <Route exact path="/home/referrals" component={ReferralsPage} />
            <Route exact path="/home/diary/add" component={AddDiaryPage} />
            <Route exact path="/home/diary/:id" component={AddDiaryPage} />
            <Route exact path="/home/admin/referrals">
              <Redirect to="/admin/referrals" />
            </Route>
            <Route exact path="/home/admin/support">
              <Redirect to="/support-admin" />
            </Route>
            <Route exact path="/home/support" component={SupportPage} />
            <Route
              exact
              path="/home/support/:category"
              component={DynamicSupportCategoryPage}
            />
            <Route
              exact
              path="/home/support/:category/:article"
              component={DynamicSupportArticlePage}
            />
            <Route path="/home">
              <Redirect to="/home/tasks" />
            </Route>
          </Switch>
        </Box>

        <LoadingBackdrop open={busy} />
        <SetTimeZone
          open={!context.grower?.TimeZone}
          onClickOk={handleSetTimeZoneOnClickOk}
        />

        <Dialog
          fullWidth
          maxWidth="md"
          open={openOnboardingDialog}
          onClose={handleCloseOnboardingDialog}
        >
          <DialogTitle>Getting Started With SeedLeaf</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ textAlign: "center" }}>
              <YouTube
                onPlay={handleOnPlayOnboardingVideo}
                videoId="dM7KK-upBGk"
                opts={youTubeOpts}
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOnboardingDialog}>
              Snooze until next session
            </Button>
            <Button onClick={handleCompleteOnboardingDialog} color="primary">
              Complete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LowStockProvider>
  );
}
